import React, { useState, useEffect, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faCheck, faTimes, faCopy, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { customStyles } from '../../tableStyles';
import axios from 'axios';
import '../css/PersonList.css';
import '../css/EmailCopyControl.css';
import '../css/CrmSearchBar.css';
import { dedupePersonRoleDisplay } from '../../utils/dedupePersonRoles';
import DeleteConfirmModal, { useDeleteConfirm } from '../ems/DeleteConfirmModal';
import { DataTableLoader, GoldLoader } from '../shared/Loaders';

function PersonList() {
  const [persons, setPersons] = useState([]);
  const [filteredPersons, setFilteredPersons] = useState([]);
  const [organisations, setOrganisations] = useState({});
  const [roles, setRoles] = useState({});
  const [personRoles, setPersonRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [lookupError, setLookupError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastTone, setToastTone] = useState('ok');
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;
  const deleteConfirm = useDeleteConfirm();
  const dialogVisible = Boolean(deleteConfirm.pending);
  const toastTimerRef = useRef(null);
  const fetchGenRef = useRef(0);
  /** When search/sort changes on page > 1, skip the stale-page fetch (IND-001). */
  const pendingPageResetRef = useRef(false);

  const COLUMN_SORT_BY = {
    Name: 'name',
    Email: 'email',
  };

  const showNotice = (message, tone = 'ok', ms = 3500) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMessage(message);
    setToastTone(tone);
    setShowToast(true);
    toastTimerRef.current = setTimeout(() => setShowToast(false), ms);
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // Lookups once on mount — orgs/roles/personRoles for Organisations/Roles columns.
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [orgRes, roleRes, personRoleRes] = await Promise.all([
          axios.get(`${API}/organisations`),
          axios.get(`${API}/roles`),
          axios.get(`${API}/personRoles`),
        ]);

        const orgMap = orgRes.data.reduce((map, org) => {
          map[org._id] = org.name;
          return map;
        }, {});

        const roleMap = roleRes.data.reduce((map, role) => {
          map[role._id] = role.role_name;
          return map;
        }, {});

        setOrganisations(orgMap);
        setRoles(roleMap);
        setPersonRoles(personRoleRes.data);
        setLookupError('');
      } catch (error) {
        console.error('Error fetching lookup data:', error);
        setLookupError('Organisation/role columns unavailable — lookup failed.');
        showNotice('Organisation/role columns unavailable — lookup failed.', 'error', 5000);
      }
    };

    fetchLookups();
  }, [API]);

  // Debounce search 300ms.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset to page 1 when search or sort changes (IND-001).
  useEffect(() => {
    if (page !== 1) {
      pendingPageResetRef.current = true;
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, sortBy, sortDir]);

  // Fetch persons. Skip one frame when search/sort forces page→1 from page>1 (IND-001).
  // Ignore superseded responses (IND-001/002).
  useEffect(() => {
    if (pendingPageResetRef.current) {
      pendingPageResetRef.current = false;
      if (page !== 1) return;
    }

    const gen = ++fetchGenRef.current;
    let cancelled = false;

    const fetchPersons = async () => {
      setLoading(true);
      setListError('');
      try {
        const res = await axios.get(`${API}/persons`, {
          params: {
            page,
            limit: rowsPerPage,
            search: debouncedSearch || undefined,
            sortBy: sortBy || undefined,
            sortDir: sortBy ? sortDir : undefined,
          },
        });
        if (cancelled || gen !== fetchGenRef.current) return;
        setPersons(res.data.data || []);
        setFilteredPersons(res.data.data || []);
        setTotalRows(res.data.total || 0);
        setHasLoadedOnce(true);
      } catch (error) {
        console.error('Error fetching persons:', error);
        if (cancelled || gen !== fetchGenRef.current) return;
        setListError('Failed to load individuals. Please try again.');
        setPersons([]);
        setFilteredPersons([]);
        setTotalRows(0);
        setHasLoadedOnce(true);
        showNotice('Failed to load individuals. Please try again.', 'error', 5000);
      } finally {
        if (!cancelled && gen === fetchGenRef.current) {
          setLoading(false);
        }
      }
    };

    fetchPersons();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, sortBy, sortDir, API, refreshNonce]);

  const handleSort = (column, direction) => {
    const nextSortBy = COLUMN_SORT_BY[column?.name];
    // Server sort only for Name / Email. Org/Roles not sortable (IND-008).
    if (!nextSortBy) return;
    const nextDir = direction === 'desc' ? 'desc' : 'asc';
    setSortBy(nextSortBy);
    setSortDir(nextDir);
  };

  const copyToClipboard = async (text) => {
    const value = (text || '').trim();
    if (!value) {
      showNotice('No email to copy.', 'error');
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      showNotice('Email copied to clipboard!');
    } catch (err) {
      console.error('Copy failed:', err);
      showNotice('Failed to copy email.', 'error');
    }
  };

  const toggleXmas = async (personId) => {
    try {
      const res = await axios.put(`${API}/persons/xmas/${personId}`);
      setPersons((prev) =>
        prev.map((p) => (p._id === personId ? { ...p, xmas1: res.data.xmas1 } : p))
      );
      setFilteredPersons((prev) =>
        prev.map((p) => (p._id === personId ? { ...p, xmas1: res.data.xmas1 } : p))
      );
      showNotice(res.data.message || 'Xmas card status updated.');
    } catch (err) {
      console.error('Error toggling Xmas card:', err);
      showNotice('Failed to update Xmas card.', 'error');
    }
  };

  const toggleCeoXmas = async (personId) => {
    try {
      const res = await axios.put(`${API}/persons/ceoxmas/${personId}`);
      setPersons((prev) =>
        prev.map((p) => (p._id === personId ? { ...p, xmas2: res.data.xmas2 } : p))
      );
      setFilteredPersons((prev) =>
        prev.map((p) => (p._id === personId ? { ...p, xmas2: res.data.xmas2 } : p))
      );
      showNotice(res.data.message || 'CEO Xmas card status updated.');
    } catch (err) {
      console.error('Error toggling CEO Xmas card:', err);
      showNotice('Failed to update CEO Xmas card.', 'error');
    }
  };

  // Bulk copy spans entire DB (not filtered by current search) — IND-019.
  const copyXmasEmailsByFlag = async (flag) => {
    try {
      const res = await axios.get(`${API}/persons/xmas-emails`, { params: { flag } });
      const emails = res.data || [];
      if (emails.length === 0) {
        showNotice('No emails to copy.');
        return;
      }
      await navigator.clipboard.writeText(emails.join(', '));
      showNotice('Emails copied to clipboard!');
    } catch (err) {
      console.error('Error copying xmas emails:', err);
      showNotice('Failed to copy Xmas emails.', 'error');
    }
  };

  const copyXmasEmails = () => copyXmasEmailsByFlag('xmas1');
  const copyCeoXmasEmails = () => copyXmasEmailsByFlag('xmas2');

  const getRelationships = (personId) => {
    const relations = personRoles.filter(
      (role) => String(role.person_id) === String(personId)
    );
    return dedupePersonRoleDisplay(relations, organisations, roles);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRowClick = (row) => {
    navigate(`/edit-person/${row._id}`, { state: { person: row } });
  };

  const personDisplayName = (person) =>
    [person?.name, person?.mname, person?.lname].filter(Boolean).join(' ') || 'this person';

  const openDialog = (person) => {
    deleteConfirm.ask({ id: person._id, name: personDisplayName(person) });
  };

  const confirmDelete = async () => {
    const id = deleteConfirm.pending?.id;
    if (!id) return;
    deleteConfirm.setBusy(true);
    try {
      await axios.delete(`${API}/persons/${id}`);
      deleteConfirm.clear();
      const isLastRowOnPage = persons.length === 1 && page > 1;
      if (isLastRowOnPage) {
        setPage(page - 1);
      } else {
        setRefreshNonce((n) => n + 1);
      }
    } catch (error) {
      console.error('Error deleting person:', error);
      deleteConfirm.setError('Failed to delete person.');
      deleteConfirm.setBusy(false);
    }
  };

  const emptyMessage = listError
    ? listError
    : debouncedSearch
      ? 'No individuals match your search.'
      : 'No individuals yet.';

  const columns = [
    {
      name: 'Image',
      selector: (row) => row.image,
      width: '72px',
      left: true,
      cell: (row) => (
        <div className="person-list-cell-image">
          <img
            src={row.image ? `/images/${row.image}` : '/images/avatar.png'}
            alt=""
            width={44}
            height={44}
            className="person-list-avatar"
          />
        </div>
      ),
      ignoreRowClick: true,
    },
    {
      name: 'Name',
      id: 'name',
      selector: (row) => `${row.name} ${row.mname || ''} ${row.lname}`,
      sortable: true,
      left: true,
      minWidth: '160px',
      grow: 1,
      cell: (row) => (
        <button
          type="button"
          className="person-list-name-link"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(row);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              handleRowClick(row);
            }
          }}
        >
          {personDisplayName(row)}
        </button>
      ),
    },
    {
      name: 'Email',
      id: 'email',
      selector: (row) => row.email,
      sortable: true,
      minWidth: '200px',
      grow: 1.2,
      left: true,
      cell: (row) => {
        const hasEmail = Boolean((row.email || '').trim());
        return (
          <div className="email-copy-cell">
            <span className="email-text">{row.email || '—'}</span>
            <button
              type="button"
              className="email-copy-btn"
              disabled={!hasEmail}
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(row.email);
              }}
              title={hasEmail ? 'Copy Email' : 'No email'}
              aria-label={hasEmail ? 'Copy email' : 'No email to copy'}
            >
              <FontAwesomeIcon icon={faCopy} style={{ color: hasEmail ? '#D9A128' : '#bbb' }} />
            </button>
          </div>
        );
      },
    },
    {
      name: 'Organisations',
      selector: (row) =>
        getRelationships(row._id)
          .map((relation) => relation.organisation)
          .join(', '),
      sortable: false,
      left: true,
      grow: 2,
      minWidth: '140px',
      cell: (row) => {
        const relationships = getRelationships(row._id);
        if (lookupError) {
          return <span className="person-list-muted">—</span>;
        }
        return (
          <ul className="person-list-cell-list">
            {relationships
              .filter((r) => r.organisation)
              .map((relation, index) => (
                <li key={index}>{relation.organisation}</li>
              ))}
          </ul>
        );
      },
    },
    {
      name: 'Roles',
      selector: (row) =>
        getRelationships(row._id)
          .map((relation) => relation.role)
          .join(', '),
      sortable: false,
      left: true,
      grow: 1,
      minWidth: '110px',
      cell: (row) => {
        const relationships = getRelationships(row._id);
        if (lookupError) {
          return <span className="person-list-muted">—</span>;
        }
        return (
          <ul className="person-list-cell-list">
            {relationships.map((relation, index) => (
              <li key={index}>{relation.role}</li>
            ))}
          </ul>
        );
      },
    },
    {
      name: (
        <div className="person-list-header-with-copy">
          <span>Xmas Cards</span>
          <button
            type="button"
            className="person-list-icon-btn"
            onClick={(e) => {
              e.stopPropagation();
              copyXmasEmails();
            }}
            title="Copy all Xmas emails (ignores search)"
          >
            <FontAwesomeIcon icon={faCopy} style={{ color: '#fff' }} />
          </button>
        </div>
      ),
      width: '155px',
      minWidth: '155px',
      center: true,
      button: true,
      sortable: false,
      ignoreRowClick: true,
      cell: (row) => (
        <div className="person-list-cell-center">
          <button
            type="button"
            className={`person-list-xmas-toggle ${row.xmas1 ? 'person-list-xmas-toggle--on' : 'person-list-xmas-toggle--off'}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleXmas(row._id);
            }}
            title={row.xmas1 ? 'Remove from Xmas Cards' : 'Add to Xmas Cards'}
          >
            <FontAwesomeIcon icon={row.xmas1 ? faCheck : faTimes} />
          </button>
        </div>
      ),
    },
    {
      name: (
        <div className="person-list-header-with-copy">
          <span>CEO Xmas Cards</span>
          <button
            type="button"
            className="person-list-icon-btn"
            onClick={(e) => {
              e.stopPropagation();
              copyCeoXmasEmails();
            }}
            title="Copy all CEO Xmas emails (ignores search)"
          >
            <FontAwesomeIcon icon={faCopy} style={{ color: '#fff' }} />
          </button>
        </div>
      ),
      width: '185px',
      minWidth: '185px',
      center: true,
      button: true,
      sortable: false,
      ignoreRowClick: true,
      cell: (row) => (
        <div className="person-list-cell-center">
          <button
            type="button"
            className={`person-list-xmas-toggle ${row.xmas2 ? 'person-list-xmas-toggle--on' : 'person-list-xmas-toggle--off'}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleCeoXmas(row._id);
            }}
            title={row.xmas2 ? 'Remove from CEO Xmas Cards' : 'Add to CEO Xmas Cards'}
          >
            <FontAwesomeIcon icon={row.xmas2 ? faCheck : faTimes} />
          </button>
        </div>
      ),
    },
    {
      name: 'Delete',
      width: '80px',
      minWidth: '80px',
      center: true,
      headerStyle: {
        justifyContent: 'center',
        textAlign: 'center',
      },
      cell: (row) => (
        <div className="person-list-cell-center">
          <button
            type="button"
            className="person-list-icon-btn person-list-delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              openDialog(row);
            }}
            title={`Delete ${personDisplayName(row)}`}
            aria-label={`Delete ${personDisplayName(row)}`}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
    },
  ];

  if (loading && !hasLoadedOnce) {
    return <GoldLoader label="Loading..." variant="page" />;
  }

  return (
    <div className="crm-list-page person-list-page">
      <div className="crm-list-toolbar person-list-toolbar">
        <div className="person-list-search-col crm-search-wide">
          <div className="crm-search-wrapper individuals-search-wrapper">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="crm-search-icon" />
            <input
              type="text"
              placeholder="Search by name, email, organisation, or role"
              className="crm-search-input form-control search-input"
              value={searchTerm}
              onChange={handleSearch}
              title="Search individuals"
              aria-label="Search individuals"
            />
          </div>
        </div>

        <div className="show-section">
          <span>Show</span>
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(1);
            }}
            className="show-dropdown-select"
            aria-label="Rows per page"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {listError ? (
        <div className="person-list-banner person-list-banner--error" role="alert">
          {listError}
        </div>
      ) : null}
      {lookupError && !listError ? (
        <div className="person-list-banner person-list-banner--warn" role="status">
          {lookupError}
        </div>
      ) : null}

      <div className="crm-table-wrapper">
        <DataTable
          key={rowsPerPage}
          columns={columns}
          data={filteredPersons}
          progressPending={loading}
          progressComponent={<DataTableLoader />}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={rowsPerPage}
          paginationDefaultPage={page}
          paginationComponentOptions={{ noRowsPerPage: true }}
          onChangePage={(newPage) => setPage(newPage)}
          sortServer
          onSort={handleSort}
          defaultSortFieldId={sortBy || undefined}
          defaultSortAsc={sortDir !== 'desc'}
          highlightOnHover
          striped
          responsive
          persistTableHead
          customStyles={customStyles}
          onRowClicked={handleRowClick}
          noDataComponent={
            <div className="person-list-empty" role="status">
              {emptyMessage}
            </div>
          }
        />
      </div>

      {dialogVisible && (
        <DeleteConfirmModal
          name={deleteConfirm.pending.name}
          error={deleteConfirm.error}
          busy={deleteConfirm.busy}
          detail="This also deletes their organisation roles, assistant relationships, and linked EMS participant records."
          onConfirm={confirmDelete}
          onCancel={deleteConfirm.clear}
        />
      )}

      {showToast && (
        <div
          className={`copy-toast ${toastTone === 'error' ? 'copy-toast--error' : ''}`}
          role="status"
        >
          <FontAwesomeIcon
            icon={toastTone === 'error' ? faTimes : faCheck}
            style={{ marginRight: '10px', color: toastTone === 'error' ? '#ff6b6b' : '#D9A128' }}
          />
          {toastMessage}
        </div>
      )}
    </div>
  );
}

export default PersonList;
